import { DIALOG_DATA, Dialog, DialogRef } from '@angular/cdk/dialog';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DriveService } from '@app/core/services';
import { notNull, safeEmptyList, switchDelay, debounceDelay } from '@app/shared/utils/operators';
import { BreadcrumbFolderService } from '@app/modules/services/components/modal-drive/breadcrumb-folder.service';
import { ModalVideoPlayerComponent } from '../modal-video-player/modal-video-player.component';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { last } from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { SHARED_MODULES } from '@app/shared';

const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'm4v', 'avi', 'mkv']);

@Component({
  templateUrl: './modal-recordings-jitsi.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BreadcrumbFolderService],
  imports: [SHARED_MODULES],
})
export class ModalRecordingsJitsiComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly dialogRef = inject<DialogRef<string>>(DialogRef);
  public readonly data = inject(DIALOG_DATA);

  public data$: Observable<any>;
  public breadcrumb$: Observable<any>;

  private _load$ = new Subject<boolean>();
  private _refresh$ = new BehaviorSubject<string>(null);
  private readonly _destroy = inject(DestroyRef);
  private readonly dialog = inject(Dialog);
  private readonly driveService = inject(DriveService);
  private readonly breadcrumbFolderService = inject(BreadcrumbFolderService);
  private readonly translateService = inject(TranslateService);

  public ngOnInit(): void {
    this._refresh$.next(this.data.path);
    this.breadcrumbFolderService.breadcrumb({
      name: this.translateService.instant('lives.gravacoes'),
      path: this.data.path,
    });
    this.breadcrumb$ = this.breadcrumbFolderService.breadcrumb$;
    this.loadData();
  }

  public ngAfterViewInit(): void {
    this.breadcrumbFolderService.navigate$
      .pipe(takeUntilDestroyed(this._destroy), distinctUntilChanged())
      .subscribe((path) => this._refresh$.next(path));
  }

  public ngOnDestroy(): void {
    this._load$.complete();
    this._refresh$.complete();
  }

  public onView(element: any): void {
    if (!element.extensao || element.extensao === 'pasta') {
      this._refresh$.next(element.caminho);
      this.breadcrumbFolderService.append(element.caminho);
    } else if (VIDEO_EXTENSIONS.has(element.extensao?.toLowerCase())) {
      this.dialog.open(ModalVideoPlayerComponent, {
        data: { url: element.url, name: this.nameElement(element.caminho) },
        width: '80vw',
      });
    } else {
      window.open(element.url, '_blank');
    }
  }

  public onNavigate(url: string, index: number): void {
    this.breadcrumbFolderService.navigate(url, index);
  }

  public nameElement(path: string): string {
    return last(path.split('/').filter(Boolean));
  }

  public isVideo(extension: string): boolean {
    return VIDEO_EXTENSIONS.has(extension?.toLowerCase());
  }

  public color(extension: string): string {
    if (!extension || extension === 'pasta') {
      return 'bg-modules-eclass-500';
    }
    if (VIDEO_EXTENSIONS.has(extension.toLowerCase())) {
      return 'bg-modules-eclass-600';
    }
    return 'bg-modules-eclass-600';
  }

  private loadData(): void {
    const load$ = this._load$.pipe(switchDelay());
    const storage$ = this.loadStorage();
    this.data$ = combineLatest({ load: load$, storage: storage$ }).pipe(
      map(({ load, storage }) => ({ load, ...storage })),
      startWith({ load: true, storage: [] }),
    );
  }

  private loadStorage(): Observable<any> {
    return this._refresh$.pipe(
      notNull(),
      distinctUntilChanged(),
      debounceDelay(),
      takeUntilDestroyed(this._destroy),
      tap(() => this._load$.next(true)),
      switchMap((path) =>
        this.driveService.files({ nome_pasta: path, external: false, bucket: 'recording' }).pipe(
          safeEmptyList(),
          map((storage) => ({ storage })),
        ),
      ),
      tap(() => this._load$.next(false)),
    );
  }
}
